'use client'

import { ArrowRight, ArrowUpRight, CalendarCheck, Check, Mail, Minus } from 'lucide-react'
import Link from 'next/link'
import type { ReactNode } from 'react'

import { PlanSoldOut } from '@/components/molecules/plan-sold-out'
import { TiltCard } from '@/components/molecules/tilt-card.client'
import { creatorHref } from '@/lib/creator'
import { formatDayTime } from '@/lib/dates'
import { splitPrice } from '@/lib/money'
import { isTopTier, tierTone } from '@/lib/offer-visuals'
import {
  type ComparisonRow,
  type PlanView,
  type ServiceView,
  sortRowsForTier,
} from '@/lib/services'
import { PICKED_RING, RADIUS } from '@/lib/surface'
import { TIER_FIT_VARS } from '@/lib/tier-fit'
import { BODY, CARD_TITLE, KICKER, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * One level of an offer, as a card.
 *
 * ## Why it is here and not in the two pages
 *
 * This markup existed TWICE — `montage-page.client.tsx` and
 * `graphics-page.client.tsx` — identical down to the `mt-0.5` on the tick, and
 * it renders four routes: montaż, rolki, miniaturki and grafika's packages. A
 * design change had to be made in two files and could only be half made, which
 * is how the two ended up agreeing on everything except the things nobody
 * checked.
 *
 * ## What the rebuild changed
 *
 * The old card was a dark slab holding a name, a price and fourteen rows of
 * 12px text, each one a tick or an empty circle. Three of them side by side had
 * no hierarchy at all: the same fill, the same border, the same list, and the
 * ladder they describe — Clean, Dynamic, Advanced — was something you worked out
 * by reading three prices. Four things carry it now.
 *
 * **The rung glyph.** Ascending bars filled to this level, in this level's hue.
 * It says "2 of 3" before a word has been read.
 *
 * **The hue.** {@link tierTone} walks the brand gradient across the ladder, and
 * the card is lit by a pool of it from the top left. Three cards that differ in
 * temperature read as a progression; three identical ones read as a table.
 *
 * **The two groups.** What you get, then what the next level adds — under their
 * own labels with their own counts, instead of one list where the second half
 * is dimmed. Nine greyed rows of "not included" read as a list of failures; the
 * same nine under "wyżej w cenniku" read as the reason to step up, which is
 * what they actually are. Nothing was removed: every row the matrix carries is
 * still on every card, and the excluded ones keep the screen-reader sentence
 * that says so.
 *
 * **The tick.** In the tier's own colour, not the `oklch(0.78 0.17 150)` green
 * that appeared nowhere else on the site.
 *
 * ## One filled button per grid
 *
 * The recommended tier keeps the brand gradient; its neighbours went from solid
 * white — the loudest thing on a dark page, twice — to glass. A pricing grid
 * has one recommendation, so it should have one filled button.
 */
export function TierCard({
  locale,
  service,
  plan,
  level,
  levels,
  pack,
  tilt = true,
}: {
  locale: string
  /** The offering this level belongs to — its slug, its rows, its default qty. */
  service: ServiceView
  plan: PlanView
  /** 1-based rung on this ladder; what the glyph fills to. */
  level: number
  /** How many rungs there are. */
  levels: number
  /**
   * The ten-reel package, when the page's switch is on it: the card then sells
   * ten of this level at the package price and hands the creator a quantity of
   * ten, which is what makes the checkout price it as a package.
   */
  pack?: { size: number; price: string; listPrice: string; perUnit: string } | null
  /**
   * Whether the card leans toward the pointer on its own. `/pl/montaz` turns it
   * off: there the whole row leans together (`TiltGroup`) and a frame glow sits
   * outside the card, so a card tilting inside its frame would slide off it.
   */
  tilt?: boolean
}) {
  const tone = tierTone(level, levels)
  /** The level the offer wants sold. It gets the gradient, not just a hue. */
  const top = isTopTier(level, levels)
  /**
   * EVERY row on every card, what this level carries first and what it does
   * not after — one list, not two groups under two headings.
   *
   * The two-group version put the missing rows under their own label, which
   * made them a second subject the reader had to take in. As one sorted list
   * with the tail struck through, the card says the same thing in one gesture:
   * here is what you get, and here, crossed out, is what you are leaving on
   * the table.
   *
   * The trade is that the three cards no longer line up row for row — a tier
   * that carries four rows strikes eight, and its neighbour strikes none, so
   * the same feature sits at a different height on each card. Reading DOWN one
   * card is what this order is for; reading ACROSS three is what the matrix
   * further down the page does properly.
   */

  const sorted = sortRowsForTier(
    service.rows.filter((row) => !DELIVERY_LABELS.has(row.label)),
    plan.name,
  )
  /**
   * Measured rows first, flat ones after them, excluded ones last.
   *
   * Owner: *„niech te rzeczy bez wykresu liniowego będą pod tymi z wykresami"*.
   * A rule under a row is a visual rhythm, and one tick row dropped in the
   * middle of three of them breaks it for no reason — the eye reads a gap as a
   * grouping that means something. Gathered at the foot they read as what they
   * are: the things that are simply in the package, with nothing to measure.
   *
   * `sortRowsForTier` has already put the excluded rows at the back, and this
   * sort is stable, so that stays true; it only reorders inside each half.
   */
  const rows = [...sorted].sort((a, b) => {
    const aOut = a.values[plan.name] === false
    const bOut = b.values[plan.name] === false
    if (aOut !== bOut) return aOut ? 1 : -1
    const aDrawn = measuredShare(a, plan.name) !== null
    const bDrawn = measuredShare(b, plan.name) !== null
    if (aDrawn !== bDrawn) return aDrawn ? -1 : 1
    return 0
  })
  const included = rows.filter((row) => row.values[plan.name] !== false)
  /**
   * The name of the top level, so a missing row can say what HAS it.
   *
   * "Napisy" greyed out tells the reader they are not getting something; the
   * same row reading "Napisy — w Pro: dynamiczne + efekt wow" tells them what
   * they are not getting, which is the only version of that sentence anybody
   * can act on. The matrix already holds every value; nothing new is loaded.
   */
  const topName = service.plans.at(-1)?.name ?? plan.name
  /**
   * Rows this level HAS, but weaker than the top level has them.
   *
   * Without this the card lies by omission. On `rolki` the middle level
   * carries all twelve rows and so does the top one — the ladder is built out
   * of GRADES ("Sound design · Rozbudowany" against "· Premium"), not out of
   * presence — so the coverage meter reads "12 z 12" on both and quietly says
   * the two levels are the same package. They are not, and the difference is
   * the entire reason the top one costs more.
   *
   * Counted, not listed: every one of them is already on the card with its own
   * value beside it. One line naming how many is the nudge; repeating twelve
   * rows would be the table this card exists to replace.
   */
  const upgrades = included.filter((row) => {
    const mine = row.values[plan.name]
    const best = row.values[topName]
    return typeof best === 'string' && typeof mine === 'string' && mine !== best
  }).length

  /**
   * How full this level is, measured against the TOP level rather than against
   * a row count — the number the meter at the head of the card fills to.
   *
   * Plain presence cannot carry it. On `rolki` the middle level and the top one
   * both hold all fourteen rows, so a share of `included / rows` paints both
   * bars to the end and tells the reader the two packages are the same. They
   * are not: the ladder there is built out of GRADES, and a meter that cannot
   * see a grade is a meter that argues against the level it is supposed to sell.
   *
   * So a row counts WHOLE when this level has it exactly as the top level has
   * it, and HALF when it has it in a weaker grade. Half rather than some tuned
   * fraction because there is no scale behind „Rozbudowany" against „Premium" —
   * one is more than the other and that is all the data says. Owner asked for
   * the bar to read like the length row it sits above (35 s, 45 s, 60 s against
   * a full 60), and halving lands the three levels in the same shape without
   * inventing a ranking nobody wrote down.
   */
  /** Is every row this level carries drawn as a measure? */
  const allMeasured =
    included.length > 0 && included.every((row) => measuredShare(row, plan.name) !== null)

  return (
    <MaybeTilt on={tilt}>
      <article
        className={cn(
          // 150ms, not 300. Moving a pointer across a pricing grid is a
          // high-frequency interaction — three cards light and unlight as the
          // cursor crosses them — and at 300ms the border is still catching up
          // with a cursor that has already left. Colour only, so there is
          // nothing here for reduced motion to stop.
          '@container group relative flex h-full flex-col overflow-hidden border transition-colors duration-150',
          'p-[var(--tier-pad)]',
          // Every vertical measurement below reads from these; a card is
          // therefore correct on a 1440 monitor and on a 13" laptop without
          // either page knowing which it is on. See `lib/tier-fit.ts`.
          TIER_FIT_VARS,
          RADIUS.feature,
          // The ring is the ladder's crown and the badge is the offer's own
          // recommendation; they are different claims and either can be true.
          // The top level wears the ring whether or not it is the popular one.
          plan.popular || top
            ? PICKED_RING
            : 'border-white/12 bg-white/[0.05] hover:border-white/25 hover:bg-white/[0.08]',
        )}
      >
        {/* The light, as a layer rather than as a background.
            `PICKED_RING` paints its fill and its gradient border through the
            `background` shorthand — a second background on the same element
            would overwrite one of them and the recommended card would lose
            either its glass or its ring. An absolutely positioned span cannot
            collide with either. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-48"
          style={{
            // The top rung is lit hardest, which is the whole escalation: grey
            // card, violet card, card with the light on. See `tierTone`.
            background: `radial-gradient(120% 100% at 24% 0%, color-mix(in oklab, ${tone} ${
              top ? 34 : plan.popular ? 24 : 15
            }%, transparent), transparent 70%)`,
          }}
        />
        <span
          aria-hidden="true"
          className={cn(
            // `RADIUS.feature`, not a second `rounded-3xl` written out: this
            // span is the card's own corner and has to follow it if the token
            // ever moves.
            RADIUS.feature,
            'tilt-sheen pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-150 [.tilt-card:hover_&]:opacity-100',
          )}
        />

        {/* The travelling glow, on the recommended card only. Defined in
            `globals.css` as `.flou-sweep`, because it is a keyframe animation
            and Tailwind cannot express one. The card is already
            `overflow-hidden`, which is what clips the band to the corner
            radius on its way past. */}
        {plan.popular || top ? <span aria-hidden="true" className="flou-sweep" /> : null}

        <div className="relative flex flex-1 flex-col">
          <div className="mt-[var(--tier-tick)] flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2.5">
              <TierRank level={level} levels={levels} tone={tone} top={top} />
              <h2 className={cn(CARD_TITLE, 'truncate text-[1.35rem] text-white')}>{plan.name}</h2>
            </span>
            {/* White pill, dark text — an INVERTED control, the same device the
                solid buttons use. It was a gradient pill in caps, which put a
                third gradient on a card that already carries one in its ring
                and one in its rank glyph; the badge stopped reading as the
                loudest thing on the card, which is its only job. */}
            {plan.popular ? (
              <span
                className={cn(
                  RADIUS.pill,
                  'inline-flex shrink-0 items-center bg-white px-3 py-1 text-[0.8rem] font-semibold text-neutral-900',
                )}
              >
                Polecany
              </span>
            ) : null}
          </div>

          {/* THE NUMBER IS THE CARD. It used to be one line among seven, set
              at `2.6rem` between a description and a divider, and three cards
              side by side asked the reader to compare three paragraphs to find
              three figures. It is now the first thing the eye lands on and the
              only thing set at display size.

              A quote-only level is NOT a number — "Wycena indywidualna" at the
              price size wraps to three lines and pretends to be one — so it is
              set as the sentence it is.

              The units ride in a column beside the figure rather than on lines
              of their own: `zł + VAT` at the top of the digits and the unit at
              their foot. That folded away the separate "netto · + VAT" line,
              which said the same thing a whole row lower. */}
          <div className="mt-[var(--tier-step)]">
            {plan.quoteOnly ? (
              <p className="text-[1.3rem] leading-tight font-bold tracking-tight text-white">
                {plan.price}
              </p>
            ) : (
              <PriceFigure
                price={pack ? pack.price : plan.price}
                suffix={pack ? `/ ${pack.size} rolek` : plan.suffix}
                tone={tone}
                top={top}
              />
            )}
          </div>

          <p className={cn(BODY, 'mt-[var(--tier-tick)] text-white/55')}>
            {plan.quoteOnly ? (
              'Zakres decyduje o cenie — odpiszemy z wyceną.'
            ) : pack ? (
              <>
                <span className="line-through">{pack.listPrice}</span> · {pack.perUnit} za rolkę
              </>
            ) : (
              plan.description
            )}
          </p>

          <div className="mt-[var(--tier-step)] flex flex-1 flex-col gap-[var(--tier-step)] border-t border-white/10 pt-[var(--tier-step)]">
            <FeatureGroup label="W pakiecie" tone={tone}>
              {rows.map((row, index) => (
                <FeatureRow
                  key={row.label}
                  row={row}
                  tier={plan.name}
                  tone={tone}
                  topName={topName}
                  /* The first row with nothing to draw opens the flat half of
                     the list and wears the divider. Owner: *„wyróżnijmy jakoś
                     wizualnie te z paskiem i te bez, bo się zlewa"* — and they
                     did: a measured row is a name, a figure and a rule, a flat
                     one is a name, and stacked without a break the reader took
                     the fourth line for a fourth measurement whose bar had
                     failed to load. */
                  opensFlatHalf={
                    index > 0 &&
                    measuredShare(row, plan.name) === null &&
                    measuredShare(rows[index - 1] as ComparisonRow, plan.name) !== null
                  }
                />
              ))}
            </FeatureGroup>

            {/* The sentence is for a ladder built out of GRADES with nothing to
                draw — „Sound design · Rozbudowany" against „· Premium" is a step
                only words can name. Where every row the level carries is a
                measure, the rules under them already show the same gap, to the
                pixel, against a track that ends at the top level. Saying it
                again in words is the card arguing with itself. */}
            {!top && upgrades > 0 && !allMeasured ? (
              <p className={cn(MICRO, 'flex items-start gap-2 text-white/50')}>
                <ArrowUpRight
                  size={13}
                  strokeWidth={1.5}
                  absoluteStrokeWidth
                  className="mt-[1px] shrink-0"
                  style={{ color: tone }}
                  aria-hidden="true"
                />
                <span>
                  {upgrades} z nich jest mocniejszych w{' '}
                  <span className="font-semibold text-white/70">{topName}</span>.
                </span>
              </p>
            ) : null}
          </div>

          {/* A level with no price cannot go to the creator: the creator exists
              to multiply a rate by a quantity and there is no rate. It carries
              the service slug into the contact form so the desk reads what
              somebody asked about instead of asking.

              No calendar, no sale — `PlanSoldOut`, the shop's standing rule. */}
          {plan.quoteOnly ? (
            <CtaLink
              href={`/${locale}/kontakt?usluga=${encodeURIComponent(service.slug)}`}
              filled={plan.popular}
              icon={<Mail size={14} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />}
            >
              Zapytaj o wycenę
            </CtaLink>
          ) : plan.readyFrom === null ? (
            <PlanSoldOut />
          ) : (
            <>
              <CtaLink
                href={creatorHref(locale, {
                  offeringSlug: service.slug,
                  variantSlug: plan.slug,
                  qty: pack ? pack.size : service.defaultQty,
                })}
                filled={plan.popular}
                trailing={
                  <ArrowRight size={14} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
                }
              >
                Dodaj do koszyka
              </CtaLink>
              {/* THE COLLECTION DAY, and nothing else. „Start od" stood here
                  for a year and answered a question about FLOU's calendar —
                  when somebody could begin. Owner, 14.10: *„usuwamy »Start od«,
                  interesuje nas tylko termin odbioru"*. The person reading three
                  prices is asking when THEY get the file, and the start date
                  made them do the arithmetic themselves.

                  „Najpóźniej" is load-bearing, and it was wrong the first way
                  round: 48 h is a promise, so the date is the far end of the
                  window, not the near one. Owner put it plainly — *„to nie jest
                  najszybszy odbiór, tylko wręcz przeciwnie, najpóźniejszy"*. The
                  slot still moves as other orders take capacity, so this is the
                  deadline for an order placed NOW. */}
              {plan.deliveryAt ? (
                <p
                  className={cn(
                    MICRO,
                    'mt-[var(--tier-tick)] flex items-center justify-center gap-1.5 text-white/70',
                  )}
                >
                  <CalendarCheck
                    size={12}
                    strokeWidth={1.5}
                    absoluteStrokeWidth
                    style={{ color: tone }}
                    aria-hidden="true"
                  />
                  <span>
                    Odbiór najpóźniej{' '}
                    <span className="font-semibold text-white/90">
                      {formatDayTime(plan.deliveryAt)}
                    </span>
                  </span>
                </p>
              ) : null}
            </>
          )}
        </div>
      </article>
    </MaybeTilt>
  )
}

/** The card's own lean, or a plain box of the same height when the page leans it. */
function MaybeTilt({ on, children }: { on: boolean; children: ReactNode }) {
  return on ? (
    <TiltCard className="h-full" strength={5}>
      {children}
    </TiltCard>
  ) : (
    <div className="h-full">{children}</div>
  )
}

/* ─────────────────────────────────────────────────────────── */

/**
 * The price, set in the masthead face.
 *
 * Third face in this spot and the last one. StretchPro was here first and lost
 * on legibility — a poster face has no aperture at 2rem and the figures read as
 * one band. Archivo at 125% of its width axis replaced it and did the job, but
 * it was the site's FOURTH typeface, loaded for three numbers on two pages; a
 * price is not a different voice from the page's own title, and keeping a whole
 * family to say so was the wrong trade.
 *
 * Outfit is geometric, so its figures are near-circular and hold apart at a
 * glance — which is the property the prices actually needed, and the one
 * StretchPro failed. `tabular-nums` keeps the three cards' digits on the same
 * columns.
 *
 * Currency in the body font at half the size, as a price is normally set, and
 * `plan.suffix` ("/ min") already sits beside it in the body font.
 *
 * `2.6rem`, back up from the `2.3rem` the expanded grotesque needed: Archivo at
 * full width ran about 1.4× the width of Geist per digit, so the number had been
 * cut to stop a four-figure price crossing the card. Outfit sets narrower and
 * the old size fits again.
 */
function PriceFigure({
  price,
  suffix,
  tone,
  top,
}: {
  price: string
  /** "/ rolka", "/ min" — the unit the figure is per. */
  suffix: string
  tone: string
  /**
   * The top rung sets its figure IN the brand gradient.
   *
   * It is the loudest thing the site can do to a number, and it is spent here
   * on purpose: the whole ladder exists to carry a reader up to this level, so
   * the level at the top says so with the brand's own colour while the two
   * under it stay white. Two gradient numbers in a row would be no ladder.
   */
  top?: boolean
}) {
  const { amount, currency } = splitPrice(price)

  return (
    <div className="flex items-stretch gap-2.5">
      {/* `--tier-price` is `min(24cqw, 6.4svh)` — see `lib/tier-fit.ts`.

          `cqw`, not `vw`: the card is the thing that changes width here — the
          rail runs three across at `lg` and gains room again at `2xl`, while
          the viewport says nothing about which. That ceiling is set by the
          widest figure the catalogue can produce, not by the prettiest: four
          digits at the top of the range still have to sit on one line.

          Measured rather than guessed: three digits set 1.69× the font size
          wide, so at the 448px card the rail gives at `xl` the ceiling leaves
          about 100px of slack with a four-figure price in it.

          The SECOND cap is the screen's height, and it is the one that binds on
          a laptop: at 100px this figure is the second-tallest thing on the card
          after the feature list, so a ladder that has to fit one screen cannot
          leave it sized off the width alone. */}
      <span
        className={cn(
          'font-masthead text-[length:var(--tier-price)] leading-[0.84] font-bold tracking-[-0.03em] tabular-nums',
          top ? 'bg-[image:var(--flou-gradient)] bg-clip-text text-transparent' : 'text-white',
        )}
      >
        {amount}
      </span>

      {/* Currency at the head of the digits, unit at their foot — the figure's
          own height is the column, so the two labels bracket it instead of
          stacking under it. */}
      <span className="flex min-w-0 flex-col justify-between py-[0.35em]">
        <span className="text-[1.05rem] leading-none font-semibold whitespace-nowrap text-white/85">
          {currency ? `${currency} + VAT` : '+ VAT'}
        </span>
        <span
          className="text-[0.85rem] leading-none font-semibold whitespace-nowrap"
          style={{ color: tone }}
        >
          {suffix}
        </span>
      </span>
    </div>
  )
}

/**
 * Which rung this is, as bars rather than as a number.
 *
 * The bars rise, so the glyph reads as a ladder even at 14px, and the filled
 * ones carry the level's hue — the same one lighting the card behind them. The
 * count is spoken rather than drawn for screen readers: a row of coloured
 * rectangles is not a position to anybody who cannot see it.
 */
function TierRank({
  level,
  levels,
  tone,
  top,
}: {
  level: number
  levels: number
  tone: string
  /** The top rung fills its bars with the whole gradient, not one stop of it. */
  top?: boolean
}) {
  // Keyed by HEIGHT rather than by index. Each bar is 4px taller than the one
  // before it, so the height is unique within the glyph and is what actually
  // identifies a bar — an index key on a list this shape is the lint rule's
  // whole point, even where the list cannot reorder.
  const rungs = Array.from({ length: levels }, (_, i) => ({ height: 7 + i * 4, lit: i < level }))

  return (
    <>
      <span aria-hidden="true" className="flex shrink-0 items-end gap-[3px]">
        {rungs.map((rung) => (
          <span
            key={rung.height}
            className="w-[3px] rounded-full"
            style={{
              height: `${rung.height}px`,
              background: rung.lit ? (top ? 'var(--flou-gradient)' : tone) : 'oklch(1 0 0 / 0.16)',
              boxShadow: rung.lit ? `0 0 10px -2px ${tone}` : undefined,
            }}
          />
        ))}
      </span>
      <span className="sr-only">
        Poziom {level} z {levels}:{' '}
      </span>
    </>
  )
}

/**
 * A labelled block of feature rows.
 *
 * It no longer carries the meter. That moved to the head of the card, where it
 * is read before the price; leaving a second rule and a second count here put
 * the same number on the card twice, three inches apart, and the lower one won
 * the eye because it sat on top of the list it described.
 */
function FeatureGroup({
  label,
  tone,
  children,
}: {
  label: string
  /** Present on the included group; its absence is what makes the other quiet. */
  tone?: string
  children: ReactNode
}) {
  return (
    <div>
      {/* `/45` and not `/35` on the quiet variant: measured on both card
          grounds — the plain `#0d0d0d` and the ringed card's `#150f1e` — white
          at 35% reads 3.17:1 and at 45% it reads 4.52:1, which is the first
          step over the 4.5 a 12px label has to clear. The lit variant was
          already fine at 7.3:1. */}
      <p
        className={cn(KICKER, 'flex items-center gap-2', tone ? 'text-white/60' : 'text-white/50')}
      >
        {label}
        <span aria-hidden="true" className="h-px flex-1 rounded-full bg-white/10" />
      </p>
      <ul className="mt-[var(--tier-tick)] flex flex-col gap-[var(--tier-row-gap)]">{children}</ul>
    </div>
  )
}

/**
 * One row of the comparison matrix, from this tier's point of view.
 *
 * The excluded state is a `+`, not the empty circle it used to be: an empty
 * circle is a radio button, and on a card full of choices it invites a click
 * that does nothing. A plus says "there is more of this above", which is the
 * only thing the row means.
 */
/**
 * Rows whose number must never become a bar, because MORE of it is WORSE.
 *
 * A delivery time is a measure like any other and the parser reads it happily
 * — and then paints the SLOWEST level with the fullest rule, in a column where
 * every other full rule marks the top one. The card would be saying that
 * waiting twice as long is twice as much package.
 *
 * Inverting it is no better: a full bar on the cheapest level, beside three
 * bars where full means best, reads as „that one wins” at a glance. So the row
 * keeps its figure, drops its rule, and falls under the divider with the rows
 * that have nothing to draw.
 *
 * Matched on the LABEL, because that is all a `ComparisonRow` carries — the
 * stable key stops at the database layer. Rewording the row means revisiting
 * this line, which is the cost of not threading a key through four types.
 */
/**
 * The deadline row, under both names it goes by.
 *
 * The card does not print it. It carries a SPAN („do 48 h", „2 dni robocze")
 * and the card now answers the same question with a DATE under the button —
 * owner, 14.10: *„zostaje tylko data odbioru"*. The span stays in the
 * comparison matrix further down the page, where levels are read across.
 *
 * Two labels because the row is typed into the matrix as „Gotowa w" and
 * replaced by the level's own columns as „Termin realizacji"; which one a
 * request sees depends on whether the offering is on the working clock.
 */
const DELIVERY_LABELS = new Set(['Gotowa w', 'Termin realizacji'])

const UNDRAWN_MEASURES = DELIVERY_LABELS

/**
 * The number inside a row's value, when there is exactly one to find.
 *
 * `"do 45 s"` → `45`. Returns `null` for anything that is not a plain measure —
 * `"Dynamiczne + efekt wow"` has no number, `"4K 60p"` has two — because a bar
 * drawn from a guessed figure is worse than no bar at all.
 */
function measureOf(value: string | boolean | undefined): number | null {
  if (typeof value !== 'string') return null
  const found = value.match(/\d+(?:[.,]\d+)?/g)
  if (!found || found.length !== 1) return null
  const parsed = Number(found[0].replace(',', '.'))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * How long this level's bar is on a MEASURED row, against the longest on offer.
 *
 * Owner: *„pokażmy wizualnie długość rolki sekundową za pomocą paska, gdzie
 * przy 60 s będzie najdłuższy, a 35 najkrótszy"*. „do 35 s" against „do 60 s"
 * is a quantity, and three numbers in a column are read as three numbers; the
 * same three as bars are read as a ladder, at a glance, before the digits are.
 *
 * It draws ONLY where every level on the row carries a single clean number, so
 * the graded rows („Podstawowe" / „Premium") never sprout a bar measured on
 * nothing. Units are never compared across rows — seconds against seconds in
 * one row, minutes against minutes in the next — because the share is always
 * taken inside the row it belongs to.
 */
function measuredShare(row: ComparisonRow, tier: string): number | null {
  if (UNDRAWN_MEASURES.has(row.label)) return null
  const mine = measureOf(row.values[tier])
  if (mine === null) return null

  const all = Object.values(row.values)
  if (all.some((value) => value !== false && measureOf(value) === null)) return null

  const longest = Math.max(...all.map((value) => measureOf(value) ?? 0))
  if (longest <= 0) return null

  return Math.round((mine / longest) * 100)
}

function FeatureRow({
  row,
  tier,
  tone,
  topName,
  opensFlatHalf,
}: {
  row: ComparisonRow
  tier: string
  tone: string
  /** First row below the measured ones — it carries the rule that separates them. */
  opensFlatHalf?: boolean
  /**
   * The top level's name, passed only for the rows this level does NOT carry.
   *
   * A greyed "Napisy" says the reader is missing something and stops there.
   * The same row reading "Napisy · Dynamiczne + efekt wow" says WHAT they are
   * missing, and that is the only version anybody can act on. The value comes
   * out of the matrix that is already on the card — nothing extra is loaded,
   * and nothing is claimed that the comparison table does not already say.
   *
   * The group heading above these rows names the level ("Dopiero w Pro"), so
   * the row does not repeat it.
   */
  topName?: string
}) {
  const value = row.values[tier]
  const included = value !== false
  /** What the top level puts in this row, when this one puts nothing. */
  const missing = !included && topName ? row.values[topName] : undefined
  const detail = typeof value === 'string' ? value : typeof missing === 'string' ? missing : null
  const measure = measuredShare(row, tier)

  return (
    <li
      className={cn(
        'flex items-start gap-2 text-[0.8rem] leading-snug',
        // A hairline and the breathing room above it, on the row that opens the
        // flat half. Nothing is dimmed to make the point: these rows name what
        // is IN the package and dimming them would file them as fine print.
        opensFlatHalf && 'mt-1 border-t border-white/[0.07] pt-3',
        // ── STRUCK, NOT FADED OUT ────────────────────────────────────
        // `/40` measured 3.80:1 against both card grounds, under the 4.5 this
        // 12.8px row needs — and these rows are CONTENT: they name the feature
        // somebody is not getting, which is the one thing on the card that
        // argues for the level above.
        //
        // `/50` reads 5.34:1 — one step past the first that clears 4.5, for
        // the margin the glow over the card's top can eat. Nothing is lost by
        // the change, because the dimming was never what said „taken away" —
        // the line through the words is, as the note on the span below has
        // said all along. Against `/75` on a live row, 45% still reads as
        // plainly secondary.
        included ? 'text-white/75' : 'text-white/50',
      )}
    >
      {included ? (
        <Check
          size={13}
          strokeWidth={1.5}
          absoluteStrokeWidth
          className="mt-[3px] shrink-0"
          style={{ color: tone }}
          aria-hidden="true"
        />
      ) : (
        <Minus
          size={13}
          strokeWidth={1.5}
          absoluteStrokeWidth
          className="mt-[3px] shrink-0 text-white/20"
          aria-hidden="true"
        />
      )}
      {/* STRUCK THROUGH, not merely dimmed. Dim text on a dark card reads as
          "quiet", which is how a reader treats fine print — the row has to read
          as taken away. The line carries the top level's value with it, so what
          is crossed out is the thing on offer one card to the right, at the
          same height, uncrossed. */}
      <span className={cn('min-w-0 flex-1', !included && 'line-through decoration-white/30')}>
        {/* A MEASURED row is set as a measurement: name on the left, figure
            hard against the right edge, rule underneath spanning both. Three
            cards side by side then put „35 s", „45 s" and „60 s" at the same
            x, and the eye compares them without being asked to.
            
            Every other row stays one line of prose — „Napisy · Animowane" has
            nothing to align against and a column rule would only split it. */}
        {measure !== null ? (
          <span className="flex items-baseline justify-between gap-2">
            <span>{row.label}</span>
            {detail ? <span className="shrink-0 tabular-nums text-white/70">{detail}</span> : null}
          </span>
        ) : (
          <>
            {row.label}
            {/* Both states at `/45` now. The struck value was `/25` — 2.17:1,
                the worst pair on the card and effectively unreadable — and it
                is the half of the row that says WHAT the better level does
                here. The strike-through still separates the two states. */}
            {detail ? <span className="text-white/50"> · {detail}</span> : null}
          </>
        )}
        {!included ? <span className="sr-only"> — brak w tym pakiecie</span> : null}
        {/* The rule, at 4px and fully rounded. At the 2px it started on it read
            as an underline to the text above it — a decoration — rather than as
            a quantity; a rounded bar with visible body is the first thing on the
            card that says „this is how much". The track is the longest level on
            offer, so the unlit remainder is the upgrade, drawn. */}
        {measure !== null ? (
          <span
            aria-hidden="true"
            className="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-white/[0.08]"
          >
            <span
              className="block h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500"
              style={{ width: `${measure}%`, backgroundColor: tone, opacity: included ? 1 : 0.3 }}
            />
          </span>
        ) : null}
      </span>
    </li>
  )
}

/**
 * The card's one action.
 *
 * Two live treatments and one dead one, and the difference between them has to
 * be legible from across the grid:
 *
 * - the recommended level gets the brand gradient;
 * - **every other AVAILABLE level gets solid white**;
 * - a sold-out level gets no button at all — `PlanSoldOut` and its grey chip.
 *
 * White, because grey was reading as switched off. The unrecommended levels
 * used to wear a glass chip — `border-white/20 bg-white/[0.07] text-white` —
 * sitting inside a card that is itself glass, so „Dodaj do koszyka" on Clean
 * looked like the same disabled control as „Chwilowo wyprzedane" one card over.
 * The owner's rule is the plain one: *„jeśli coś jest dostępne, niech będzie na
 * biało, na szaro zostawiamy tylko wyprzedane"*. A buyable level now says so
 * with the loudest thing on a dark card, and grey means one thing only.
 */
function CtaLink({
  href,
  filled,
  icon,
  trailing,
  children,
}: {
  href: string
  filled: boolean
  icon?: ReactNode
  trailing?: ReactNode
  children: ReactNode
}) {
  return (
    <Link
      href={href}
      className={cn(
        'mt-[var(--tier-step)] inline-flex items-center justify-center gap-2 px-5 py-[max(0.6rem,var(--tier-tick))] text-[0.85rem] font-semibold',
        // ── WHAT MOVES, FOR HOW LONG, AND FOR WHOM ──────────────────────
        // Was `transition duration-300 hover:scale-[1.02]`. Three things
        // wrong with that line and they are separate faults.
        //
        // `transition` is Tailwind's shorthand for a dozen properties —
        // colour, border, shadow, filter, backdrop-filter and the rest — so
        // the browser watched all of them to animate the two that change.
        // Named now: the scale, and the background the white variant swaps
        // on hover.
        //
        // 300ms on a BUTTON hover is slow. A pointer crossing a pricing grid
        // passes three of these, and the rule is the same one the card's own
        // border answers to: a high-frequency interaction gets 150ms or it
        // gets nothing.
        //
        // And the scale ran for everybody. `prefers-reduced-motion` exists
        // for readers who get motion sick or lose their place when a target
        // moves under the cursor, and a button that grows as you approach it
        // is exactly that. `motion-safe:` gates BOTH scales; the colour change
        // is not motion and still runs, so the button never stops answering.
        'transition-[scale,background-color] duration-150 ease-out',
        // The press. 0.96 is the whole vocabulary of "this is a real button"
        // on a touch screen, where there is no hover to say so first — and it
        // was simply missing: the card's one action had a hover state and no
        // pressed one.
        'motion-safe:hover:scale-[1.02] motion-safe:active:scale-[0.96]',
        // With a pressed state of its own, the browser's grey tap flash is a
        // second answer to one tap.
        '[-webkit-tap-highlight-color:transparent]',
        RADIUS.pill,
        filled
          ? // `--flou-gradient-solid`, not `--flou-gradient`: white on the
            // bright ramp measures 2.82:1 at its pink end against the 4.5:1
            // this 13.6px semibold label needs. The deeper ramp is the same
            // three hues at a lightness white can stand on — see the token.
            'bg-[image:var(--flou-gradient-solid)] text-white shadow-[0_14px_34px_-16px_var(--flou-glow-core)]'
          : 'border border-white bg-white text-neutral-900 shadow-[0_14px_34px_-18px_oklch(1_0_0_/_0.45)] hover:bg-white/90',
      )}
    >
      {icon}
      {children}
      {trailing}
    </Link>
  )
}
